import { EyeInvisibleOutlined } from '@ant-design/icons';
import { Alert, Heading, HelpText, Line, Link } from '@mm/components';
import { FORBIDDEN } from '@mm/constants/status-codes';
import { UTMTags } from '@mm/hooks/src/use-utm-tags';
import { EyeLight } from '@mm/icons';
import sessionCRUDActions from '@mm/state/actions/SessionCRUDActions';
import { LoginType } from '@mm/state/controllers/SessionController';
import commonHandler from '@mm/state/errorHandlers/common-handler';
import { StoreState } from '@mm/state/store-interfaces';
import { getErrorJson } from '@mm/utils';
import classNames from 'classnames';
import * as queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import FacebookLoginButton, {
  Props as FacebookLoginButtonProps,
} from '../../../../common/buttons/FacebookLoginButton/FacebookLoginButton';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import CheckBox from '../../../../common/components/Checkbox';
import TextInput from '../../../../common/components/TextInput';
import {
  EmailInput,
  useEmailInput,
} from '../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import { colors } from '../../../../editor/features/beta/styles/colors';
import { Routes } from '../../../../routes/top-level-routes';
import { routes } from '../../routes';
import { useSignup } from '../SignUpForm/SignUpForm';
import './LoginForm.pcss';

export type LoginFormProps = {
  type?: LoginType;
  heading?: string;
  subheading?: string;
  inviteToken?: string;
  facebookLoginProps?: Partial<FacebookLoginButtonProps>;
  invitingCompanyId?: number;
  invitingCompanyName?: string;
  withFacebookLogin?: boolean;
  css?: {
    container?: string;
    heading?: string;
    subheading?: string;
  };
};

export const useLogin = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const history = useHistory();

  const { signUpFacebook } = useSignup();

  const loginEmail = async (
    ...args: Parameters<typeof sessionCRUDActions.login>
  ) => {
    try {
      const session = await dispatch(sessionCRUDActions.login(...args));
      if (session) {
        const path = session.userAttributes.mmBuyBeforeTrying
          ? Routes.subscribe.getLink()
          : Routes.chatbotEditor.path;
        history.push(path);
      }
    } catch (e) {
      if (e instanceof Response) {
        const responseErrorBody = await getErrorJson(e);
        if (e.status === FORBIDDEN && responseErrorBody.errors) {
          return responseErrorBody.errors[0];
        }
      }
      return commonHandler(dispatch)(e);
    }
  };

  // login and fb signup are identical, use one handler to preserve
  // affiliate tracking & invite token workflow
  const loginFacebook = signUpFacebook;

  return {
    loginEmail,
    loginFacebook,
  };
};

export const LoginForm = ({
  css,
  type,
  heading,
  subheading = '',
  inviteToken,
  facebookLoginProps,
  withFacebookLogin = true,
}: LoginFormProps) => {
  const utmObj = UTMTags.get();

  const {
    location: { search },
  } = useRouter();

  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const { emailState, onChange } = useEmailInput();

  const [isValid, setIsFormValid] = useState<boolean>();
  useEffect(
    () => setIsFormValid(emailState.isValid && !!password.length),
    [emailState.email, emailState.isValid, password],
  );
  const [loginError, setLoginError] = useState('');

  const { loginEmail, loginFacebook } = useLogin();
  const onSubmit = async e => {
    e.preventDefault();
    const res = await loginEmail({
      email: emailState.email,
      password,
      inviteToken,
    });

    if (res) {
      setLoginError(res);
    }
  };

  useEffect(() => {
    if (type === LoginType.whiteLabel && search) {
      const { facebookToken } = queryString.parse(search);
      if (typeof facebookToken === 'string') loginFacebook({ facebookToken });
    }
  }, []);

  const signUpLinkTo =
    type === LoginType.whiteLabel || inviteToken
      ? routes.signup.compile(type) + search
      : routes.signup.compile(type);

  return (
    <form
      onSubmit={onSubmit}
      className={classNames(css?.container, 'login-form')}
    >
      {heading && (
        <Heading
          className={classNames(css?.heading, 'login-form__heading')}
          size={4}
        >
          {heading}
        </Heading>
      )}

      {subheading && (
        <Heading
          className={classNames(css?.subheading, 'login-form__subheading')}
          size={4}
        >
          {subheading}
        </Heading>
      )}

      {withFacebookLogin && (
        <FacebookLoginButton
          className="login-form__facebook-btn"
          onSuccess={({ facebookToken }) =>
            loginFacebook({
              inviteToken,
              facebookToken,
              userAttributes: {
                ...utmObj,
              },
            })
          }
          label="Log in with Facebook"
          {...facebookLoginProps}
        />
      )}

      {withFacebookLogin && (
        <Line className="login-form__separator" text="or with email" />
      )}
      {loginError && (
        <Alert className="login-form__alert" showCloseIcon={false} type="error">
          {loginError === 'Invalid credentials'
            ? 'Email or password is invalid. Please try again or Sign Up for a new account.'
            : loginError}
        </Alert>
      )}

      <div className="login-form__input-group">
        <label className="login-form__input-label">Business Email</label>
        <EmailInput
          onChange={onChange}
          inputProps={{
            className: 'login-form__input',
            placeholder: ' ',
            'data-testid': 'mmEmailInput',
          }}
          withIcon={false}
        />
      </div>

      <div className="login-form__input-group">
        <label className="login-form__input-label">Password</label>
        <TextInput
          className="login-form__input"
          onChange={e => setPassword(e.target.value)}
          aria-label="password"
          type={passwordVisible ? 'text' : 'password'}
        />
        <span
          className="login-form__password-icon"
          onClick={() => setPasswordVisible(!passwordVisible)}
        >
          {passwordVisible ? (
            <EyeLight color={colors.accentBlue} />
          ) : (
            <EyeInvisibleOutlined className="login-form__eye-invisible " />
          )}
        </span>
        <div className="login-form__secondary-actions">
          <CheckBox
            isChecked={rememberMe}
            label="Remember me"
            labelClassName="login-form__checkbox-label"
            onToggle={e => setRememberMe(e.target.checked)}
          />
          <Link
            className="login-form__link"
            to={routes.resetPassword.compile(type)}
          >
            Forgot your Password?
          </Link>
        </div>
      </div>

      <PrimaryButton
        disabled={!isValid}
        className="login-form__cta-btn"
        color={ButtonColor.blue}
        type="submit"
      >
        Log In
      </PrimaryButton>
      <div className="login-form__dashed-line" />

      <HelpText className="login-form__footer-actions">
        <span>
          Don't have an account?{' '}
          <Link className="login-form__link-button" to={signUpLinkTo}>
            Sign up
          </Link>
        </span>
      </HelpText>
    </form>
  );
};
